// The sort and filter options above the candidate list, and the rule that decides whether the
// bar shows itself as modified (`extension-alert-candidates.component.ng1.ts:293-373, 2291`).
//
// The options are DATA, transcribed verbatim, because each carries a name the user sees, a
// field the server sorts by, and — for the show filters — six booleans the query is built
// from. Getting one of those booleans wrong produces a list that is plausibly shorter or
// longer and never errors.

// ⚠️ IMPORTED, NOT REDECLARED — the same rule as `SocialHost` in `sourceOptions.ts`.
// `AlertCandidateSortBy` is what the server is sent, so it lives with the client that sends
// it; a copy here would typecheck, sort identically, and drift the first time upstream adds an
// order.
import { AlertCandidateSortBy, type AlertCandidateFilter, type ExtensionAlert } from '@woo-ext/api'

export { AlertCandidateSortBy }
export type { AlertCandidateFilter }

export interface SortOption {
  display: string
  field: AlertCandidateSortBy
}

/** `sortOptionDefault` is the FIRST entry's display, and the comparison is by that string. */
export const SORT_OPTION_DEFAULT = 'By relevance'

export const SORT_OPTIONS: readonly SortOption[] = [
  { display: SORT_OPTION_DEFAULT, field: AlertCandidateSortBy.Relevancy },
  { display: 'By last CV date', field: AlertCandidateSortBy.CvDate },
]

/** `cvDateOptions` — how recent the CV must be. */
export interface DateFilterOption {
  /** Undefined marks the SEPARATOR row, which upstream models as an option with no name. */
  name?: string
  shortName?: string
  value?: number
}

export const CV_DATE_OPTION_DEFAULT = 'Any time'

export const CV_DATE_OPTIONS: readonly DateFilterOption[] = [
  { name: CV_DATE_OPTION_DEFAULT, shortName: 'Any', value: undefined },
  // ⚠️ A SEPARATOR, NOT AN OPTION. Upstream puts `{ name: undefined, value: 9999 }` in the
  // same array and the template renders a rule for it. Filtering it out here — the obvious
  // tidy-up — removes a divider the menu is laid out around.
  { name: undefined, shortName: undefined, value: 9999 },
  { name: 'Last day', shortName: '< 1d', value: 1 },
  { name: 'Last 3 days', shortName: '< 3d', value: 3 },
  { name: 'Last 7 days', shortName: '< 7d', value: 7 },
  { name: 'Last 30 days', shortName: '< 30d', value: 30 },
  { name: 'Last 90 days', shortName: '< 90d', value: 90 },
]

/** `shownCandidatesOptions` — which candidates appear at all. */
export interface ShowFilterOption {
  name: string
  favoritesOnly: boolean
  showHidden: boolean
  hideViewedCandidates: boolean
  showExportedCandidatesOnly: boolean
  showMessagedCandidatesOnly: boolean
  showActionedAndUnactionedCandidates: boolean
  /** The icon in the OPTION LIST. */
  cssClass: string
  /**
   * ⚠️ THREE ICON FIELDS, AND ONLY THE "All" OPTION HAS MORE THAN ONE. The tooltip's selected
   * row picks `cssClassForSlimView || cssClassForSelected || cssClass`, and the row's icon is
   * suppressed entirely when `cssClassForSelected === 'empty'` and there is no slim-view
   * class. "All" carries both — `cssClassForSelected: 'empty'` and a slim-view eye — which is
   * how the full view shows no icon for the default while the slim view still shows one.
   * Collapsing these to `cssClass` puts an eye on the full view's default, where production
   * has none.
   */
  cssClassForSelected?: string
  cssClassForSlimView?: string
}

/** `cssClassForSlimView || cssClassForSelected || cssClass`, with upstream's `empty` sentinel. */
export function selectedOptionIcon(option: ShowFilterOption): string | undefined {
  if (option.cssClassForSlimView !== undefined) return option.cssClassForSlimView
  if (option.cssClassForSelected === 'empty') return undefined
  return option.cssClassForSelected ?? option.cssClass
}

export const SHOWN_OPTION_DEFAULT = 'All'

const noFlags = {
  favoritesOnly: false,
  showHidden: false,
  hideViewedCandidates: false,
  showExportedCandidatesOnly: false,
  showMessagedCandidatesOnly: false,
  showActionedAndUnactionedCandidates: false,
}

export const SHOWN_CANDIDATES_OPTIONS: readonly ShowFilterOption[] = [
  {
    name: SHOWN_OPTION_DEFAULT,
    ...noFlags,
    cssClass: 'fal fa-fw fa-eye',
    cssClassForSelected: 'empty',
    cssClassForSlimView: 'fal fa-fw fa-eye',
  },
  { name: 'Favorites', ...noFlags, favoritesOnly: true, cssClass: 'fas fa-fw fa-heart color-ext-favorite' },
  { name: 'Unviewed', ...noFlags, hideViewedCandidates: true, cssClass: 'fas fa-fw fa-eye-slash' },
  { name: 'Archived', ...noFlags, showHidden: true, cssClass: 'fas fa-fw fa-user-slash' },
]

/** Offered only when the whitelabel exports; upstream keeps it out of the base list. */
export const EXPORTED_OPTION: ShowFilterOption = {
  name: 'Exported',
  ...noFlags,
  showExportedCandidatesOnly: true,
  cssClass: 'fas fa-fw fa-upload',
}

/** Same, for messaging. */
export const MESSAGED_OPTION: ShowFilterOption = {
  name: 'Messaged',
  ...noFlags,
  showMessagedCandidatesOnly: true,
  cssClass: 'fas fa-fw fa-envelope',
}

/**
 * `All + Actioned` — behind `SHORTLIST_ACTIONED_AND_UNACTIONED_VIEW`.
 *
 * ⚠️ IT GOES AT INDEX 1, NOT THE END, and upstream says why in its own comment: "Right after
 * 'All' — it is a variant of that view, not another folder like 'Exported'/'Messaged'".
 * Appending it would read as a fourth folder.
 */
export const ACTIONED_OPTION: ShowFilterOption = {
  name: 'All + Actioned',
  ...noFlags,
  showActionedAndUnactionedCandidates: true,
  cssClass: 'fas fa-fw fa-users',
}

export interface ShownOptionsFlags {
  /** `bulkProfileActions` has Export or ExportToCsv, OR add-to-job is enabled. */
  exportCandidatesEnabled?: boolean
  addToAtsJobEnabled?: boolean
  /** `bulkProfileActions` has Message. */
  messageCandidatesEnabled?: boolean
  /** `featureFlagEnabled(ShortlistActionedAndUnactionedView)`. */
  actionedAndUnactionedEnabled?: boolean
}

/**
 * `setBulkProfileActions` — the option list the tooltip offers, assembled per tenant.
 *
 * ⚠️ "Exported" IS OFFERED WHEN ADD-TO-JOB IS ENABLED, EVEN WITH NO EXPORT ACTION. Upstream's
 * condition is `(Export || ExportToCsv) || addToAtsJobEnabled`, because adding to an ATS job
 * marks a candidate exported too. Reading it as "export is enabled" hides the folder on every
 * tenant that only adds to jobs.
 */
export function shownOptionsFor(flags: ShownOptionsFlags = {}): readonly ShowFilterOption[] {
  const options = [...SHOWN_CANDIDATES_OPTIONS]
  if (flags.exportCandidatesEnabled === true || flags.addToAtsJobEnabled === true) options.push(EXPORTED_OPTION)
  if (flags.messageCandidatesEnabled === true) options.push(MESSAGED_OPTION)
  if (flags.actionedAndUnactionedEnabled === true) options.splice(1, 0, ACTIONED_OPTION)
  return options
}

/**
 * `getNumberOfActiveFilters` (component:2291) — how many of the two differ from their default.
 *
 * ⚠️ IT COUNTS FILTERS, NOT SELECTIONS, and it compares by NAME. Both halves matter: the count
 * beside "Filters" is 0, 1 or 2 and never the number of candidates excluded, and comparing the
 * option OBJECT instead of its name would always differ, since the current option is rebuilt
 * rather than taken from the array.
 */
export function activeFilterCount(shownName: string, cvDateName: string): number {
  return Number(shownName !== SHOWN_OPTION_DEFAULT) + Number(cvDateName !== CV_DATE_OPTION_DEFAULT)
}

/** Whether the bar shows itself as modified — the same comparison, as a boolean. */
export function filtersAreModified(shownName: string, cvDateName: string): boolean {
  return activeFilterCount(shownName, cvDateName) > 0
}


/**
 * The query filter for a chosen shown-option and CV-date window — `changeShownCandidates`
 * and `syncLastRelevancyXDaysDisplay`, as one function.
 *
 * ⚠️ UPSTREAM SETS ALL SIX FLAGS EVERY TIME, including the `false` ones, and this does too.
 * Sending only the true ones looks equivalent and is not: the server merges the filter onto
 * the one it persisted for the alert, so an omitted `showHiddenCandidates` leaves yesterday's
 * `true` in place and the list silently keeps showing archived candidates after the user
 * switches back to "All".
 */
export function buildCandidateFilter(shown: ShowFilterOption, cvDateDays?: number): AlertCandidateFilter {
  return {
    showFavoriteCandidatesOnly: shown.favoritesOnly,
    showHiddenCandidates: shown.showHidden,
    hideViewedCandidates: shown.hideViewedCandidates,
    showExportedCandidatesOnly: shown.showExportedCandidatesOnly,
    showMessagedCandidatesOnly: shown.showMessagedCandidatesOnly,
    showActionedAndUnactionedCandidates: shown.showActionedAndUnactionedCandidates,
    // Absent rather than 0 for "any time": `lastRelevancyXDays: 0` is a window of zero days.
    ...(cvDateDays === undefined ? {} : { lastRelevancyXDays: cvDateDays }),
  }
}

/** The CV-date option whose `value` matches, by name. */
export function cvDateOptionByName(name: string): DateFilterOption | undefined {
  return CV_DATE_OPTIONS.find((option) => option.name === name)
}

/** The shown option by name, across the base list and the two conditional ones. */
export function shownOptionByName(name: string): ShowFilterOption | undefined {
  return [...SHOWN_CANDIDATES_OPTIONS, EXPORTED_OPTION, MESSAGED_OPTION].find((option) => option.name === name)
}

/**
 * How the alert was last left — `syncFiltersAndSorting`, which runs before the first fetch.
 *
 * ⚠️ IT READS THE ALERT, NOT LOCAL STORAGE, and it reads the flags rather than a saved
 * option name — the server persists the six booleans, so the option has to be recovered by
 * matching them. First match wins, exactly as `_.find` does: `Favorites` and `Exported`
 * differ in one flag each, so an alert with two flags set matches whichever comes first,
 * which is the option upstream shows too.
 *
 * ⚠️ AND AN UNRECOGNISED COMBINATION FALLS BACK TO THE DEFAULT RATHER THAN TO NOTHING.
 * Upstream's `syncShownOptions` does `|| this.shownCandidatesOptions[0]`: an alert persisted
 * by a newer client, with a flag this build does not offer, still renders a usable list.
 */
export function shownOptionForAlert(alert: Pick<ExtensionAlert, 'filterShowHiddenCandidates' | 'filterShowFavoritesCandidatesOnly' | 'filterHideViewedCandidates' | 'filterShowExportedCandidatesOnly' | 'filterShowMessagedCandidatesOnly' | 'filterShowActionedAndUnactionedCandidates'>): ShowFilterOption {
  const wanted = {
    favoritesOnly: alert.filterShowFavoritesCandidatesOnly === true,
    showHidden: alert.filterShowHiddenCandidates === true,
    hideViewedCandidates: alert.filterHideViewedCandidates === true,
    showExportedCandidatesOnly: alert.filterShowExportedCandidatesOnly === true,
    showMessagedCandidatesOnly: alert.filterShowMessagedCandidatesOnly === true,
    showActionedAndUnactionedCandidates: alert.filterShowActionedAndUnactionedCandidates === true,
  }
  const match = [...SHOWN_CANDIDATES_OPTIONS, EXPORTED_OPTION, MESSAGED_OPTION].find((option) =>
    (Object.keys(wanted) as (keyof typeof wanted)[]).every((key) => option[key] === wanted[key]),
  )
  return match ?? (SHOWN_CANDIDATES_OPTIONS[0] as ShowFilterOption)
}

/** The sort option for the alert's persisted `sortBy`, defaulting to relevance. */
export function sortOptionForAlert(sortBy?: AlertCandidateSortBy): SortOption {
  return (
    SORT_OPTIONS.find((option) => option.field === sortBy) ?? (SORT_OPTIONS[0] as SortOption)
  )
}

/**
 * The CV-date option for the alert's persisted window.
 *
 * ⚠️ MATCHED ON `value`, AND THE SEPARATOR'S VALUE IS 9999. It is a real entry in
 * `CV_DATE_OPTIONS` with no name, so a lookup by value has to skip it — an alert persisting
 * 9999 would otherwise display a blank filter name.
 */
export function cvDateOptionForAlert(days?: number | null): DateFilterOption {
  if (days === undefined || days === null) return CV_DATE_OPTIONS[0] as DateFilterOption
  return (
    CV_DATE_OPTIONS.find((option) => option.name !== undefined && option.value === days) ??
    (CV_DATE_OPTIONS[0] as DateFilterOption)
  )
}
